import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

/**
 * Filet de sécurité : sans lui, la moindre erreur JavaScript vide l'écran (page blanche)
 * et personne ne sait pourquoi. Ici on affiche le message + un bouton pour recharger proprement.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null; stack: string }> {
  state: { error: Error | null; stack: string } = { error: null, stack: '' };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[TAJ FUSION] écran en erreur :', error, info.componentStack);
    // On garde le début du composant fautif pour l'afficher (aide au diagnostic à distance).
    this.setState({ stack: (info.componentStack ?? '').split('\n').slice(0, 6).join('\n').trim() });
  }

  /** Vide le cache de l'application (service worker) puis recharge : répare un écran resté sur une vieille version. */
  private reset = async () => {
    try {
      const regs = await navigator.serviceWorker?.getRegistrations?.();
      await Promise.all((regs ?? []).map((r) => r.unregister()));
      const keys = await caches?.keys?.();
      await Promise.all((keys ?? []).map((k) => caches.delete(k)));
    } catch { /* pas de service worker : on recharge quand même */ }
    location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;
    const detail = [this.state.error.message, this.state.stack].filter(Boolean).join('\n');
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: '#f6efe1', fontFamily: 'Nunito, sans-serif', color: '#241c10' }}>
        <div style={{ maxWidth: 460, background: '#fffdf8', border: '1px solid #e8ddc6', borderRadius: 16, padding: 24, textAlign: 'center' }}>
          <div style={{ fontSize: '2.4rem' }}>⚠️</div>
          <h2 style={{ margin: '6px 0 4px' }}>Cet écran n'a pas pu s'afficher</h2>
          <p style={{ color: '#5f5642', fontSize: '.92rem', margin: '0 0 12px' }}>
            Essayez de recharger. Si le problème revient, envoyez ce message au support :
          </p>
          <pre style={{ background: '#fbe7ea', color: '#9B1C31', padding: 10, borderRadius: 8, fontSize: '.78rem', textAlign: 'left', overflowX: 'auto', maxHeight: 160, whiteSpace: 'pre-wrap' }}>
            {detail}
          </pre>
          <button
            onClick={this.reset}
            style={{ marginTop: 14, background: '#9B1C31', color: '#fff', border: 0, borderRadius: 10, padding: '12px 22px', fontWeight: 800, fontSize: '1rem', cursor: 'pointer' }}
          >
            🔄 Vider le cache et recharger
          </button>
        </div>
      </div>
    );
  }
}
